import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { GEDDocumento } from "../../../../common/domain/models/ged_documento";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { GedDocumentoForm } from "../schemas/GED_terceirizacao/ged_documento";
import { initialGedTerceirizacaoFilterState, useGedTerceirizacaoFilter } from "../hooks/ged_terceirizacao/filter";
import { useGedTerceirizacaoPagination } from "../hooks/ged_terceirizacao/pagination";
import { TelasIds } from "../../../../common/utils/tela_ids";

import CommonConstants from "../../../../common/constants";

/**
 * LoadDocumentosState
 */

type LoadDocumentosState = {
  tipoPesquisa?: number;
  valueField?: string;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

//useGedTerceirizacaoViewModel

export const useGedTerceirizacaoViewModel = () => {
  const {
    commons: {
      getUsuarioDropDownUseCase,
      getAllGedDocumentoUseCase,
      createGedDocumentoUseCase,
      updateGedDocumentoUseCase,
      deleteGedDocumentoUseCase,
      copyGedDocumentoUseCase,
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getGedArquivoDataUseCase,
      getGedArquivoPdfDataUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [openCadastroModal, setOpenCadastroModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openCopyModal, setOpenCopyModal] = useState(false);

  const [documentos, setDocumentos] = useState<GEDDocumento[]>();
  const [selectedDocumento, setSelectedDocumento] = useState<GEDDocumento>();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: error => pushErrorNotification("Erro", [error]),
  });

  const filters = useGedTerceirizacaoFilter();
  const pagination = useGedTerceirizacaoPagination();

  // Funções de carregamento


  const loadUsuarioDropdown = useCallback(async () => {
    try {
      const usuariosDropdown = await getUsuarioDropDownUseCase.getUsuarioDropdown();
      useGedTerceirizacaoFilter.setState({ usuariosDropdown });
    } catch (e: unknown) {
      useGedTerceirizacaoFilter.setState({ usuariosDropdown: [] });
      throw e;
    }
  }, [getUsuarioDropDownUseCase,]);

  const loadDocumentos = useCallback(async (funcState?: LoadDocumentosState) => {
    try {
      const filteredTipoPesquisa = evaluateParamValue(funcState?.tipoPesquisa, filters.tipoPesquisa);
      const filteredValueField = evaluateParamValue(funcState?.valueField, filters.valueField);
      const filteredStatus = evaluateParamValue(funcState?.status, filters.status);

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllGedDocumentoUseCase.getAllGedDocumento(
        TelasIds.GED_terceirizacao,
        CommonConstants.moduloDocumentos.Terceirizacao,
        filteredTipoPesquisa,
        filteredValueField,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setDocumentos(getAllGedDocumentoUseCase.items);

      useGedTerceirizacaoPagination.setState({ totalItems: getAllGedDocumentoUseCase.totalItems });
    } catch (e: unknown) {
      setDocumentos([]);
      throw e;
    }
  }, [
    filters,
    pagination,
    getAllGedDocumentoUseCase,
  ]);


  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadUsuarioDropdown();
      await loadDocumentos();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  // Eventos


  const handleDocumentoSubmit = useCallback(async (form: GedDocumentoForm) => {
    try {
      setSaving(true);

      if (selectedDocumento) {
        await updateGedDocumentoUseCase.updateGedDocumento(
          TelasIds.GED_terceirizacao,
          selectedDocumento.idArquivoModulo,
          form.arquivo,
          form.observacoes ?? null,
          form.file,
          form.filePdf,
          form.limparArquivo,
          form.limparArquivoPdf,
        );
      } else {
        await createGedDocumentoUseCase.createGedDocumento(
          TelasIds.GED_terceirizacao,
          CommonConstants.moduloDocumentos.Terceirizacao,
          form.arquivo,
          form.observacoes ?? null,
          form.file,
          form.filePdf,
        );
      }
      await loadDocumentos();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
    } finally {
      setSaving(false);
      setOpenCadastroModal(false);
    }
  }, [
    selectedDocumento,
    createGedDocumentoUseCase,
    updateGedDocumentoUseCase,
    loadDocumentos,
    pushErrorNotification,
  ]);

  const handleDocumentoCopySubmit = useCallback(async (form: GedDocumentoForm) => {
    try {
      setSaving(true);

      if (selectedDocumento) {
        await copyGedDocumentoUseCase.copyGedDocumento(
          TelasIds.GED_terceirizacao,
          selectedDocumento.idArquivoModulo,
          form.arquivo,
          form.observacoes ?? null,
          form.file,
          form.filePdf,
          form.limparArquivo,
          form.limparArquivoPdf,
        );
      }
      await loadDocumentos();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
    } finally {
      setSaving(false);
      setOpenCopyModal(false);
    }
  }, [
    selectedDocumento,
    copyGedDocumentoUseCase,
    loadDocumentos,
    pushErrorNotification,
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadUsuarioDropdown();
      await loadDocumentos();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadUsuarioDropdown,
    loadDocumentos,
    pushErrorNotification,
  ]);

  const handleCadastroDocumentoOpenState = useCallback((idArquivoModulo: number | null, openState: boolean) => {
    if (openState) {
      if (idArquivoModulo && documentos) {
        setSelectedDocumento(documentos.find(x => x.idArquivoModulo === idArquivoModulo));
      } else {
        setSelectedDocumento(undefined);
      }
    }
    setOpenCadastroModal(openState);
  }, [
    documentos,
  ]);

  const handleDeleteDocumentoOpenState = useCallback((idArquivoModulo: number | null, openState: boolean) => {
    if (openState) {
      if (idArquivoModulo && documentos) {
        setSelectedDocumento(documentos.find(x => x.idArquivoModulo === idArquivoModulo));
      } else {
        setSelectedDocumento(undefined);
      }
    }
    setOpenDeleteModal(openState);
  }, [
    documentos,
  ]);

  const handleConfirmarDeleteDocumento = useCallback(async () => {
    if (selectedDocumento) {
      try {
        setSaving(true);
        await deleteGedDocumentoUseCase.deleteGedDocumento(TelasIds.GED_terceirizacao, selectedDocumento.idArquivoModulo);
        await loadDocumentos();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setSaving(false);
        setOpenDeleteModal(false);
      }
    }
  }, [
    selectedDocumento,
    deleteGedDocumentoUseCase,
    pushErrorNotification,
    loadDocumentos,
  ]);

  const handleCopyDocumentoOpenState = useCallback((idArquivoModulo: number | null, openState: boolean) => {
    if (openState) {
      if (idArquivoModulo && documentos) {
        setSelectedDocumento(documentos.find(x => x.idArquivoModulo === idArquivoModulo));
      } else {
        setSelectedDocumento(undefined);
      }
    }
    setOpenCopyModal(openState);
  }, [
    documentos,
  ]);

  const handleDownloadFile = useCallback(async () => {
    if (selectedDocumento && selectedDocumento.nome) {
      try {
        setLoading(true);
        await getGedArquivoDataUseCase
          .getGedArquivoData(selectedDocumento.idArquivoModulo, selectedDocumento.nome);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    selectedDocumento,
    getGedArquivoDataUseCase,
    pushErrorNotification,
  ]);

  const handleDownloadFilePdf = useCallback(async () => {
    if (selectedDocumento && selectedDocumento.nomePdf) {
      try {
        setLoading(true);
        await getGedArquivoPdfDataUseCase
          .getGedArquivoPdfData(selectedDocumento.idArquivoModulo, selectedDocumento.nomePdf);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    selectedDocumento,
    getGedArquivoPdfDataUseCase,
    pushErrorNotification,
  ]);

  const handleTipoPesquisaChange = useDebouncedCallback(debouncedRef, async (tipoPesquisa: number) => {
    useGedTerceirizacaoFilter.setState({ tipoPesquisa, valueField: '' });
    return async () => {
      try {
        setLoading(true);
        useGedTerceirizacaoPagination.getState().setPage(1);
        await loadDocumentos({ tipoPesquisa, valueField: '', page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadDocumentos,
  ]);

  const handleValueFieldChange = useDebouncedCallback(debouncedRef, async (valueField: string) => {
    useGedTerceirizacaoFilter.setState({ valueField });
    return async () => {
      try {
        setLoading(true);
        useGedTerceirizacaoPagination.getState().setPage(1);
        await loadDocumentos({ valueField, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadDocumentos,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedRef, async (status: number) => {
    useGedTerceirizacaoFilter.setState({ status });
    return async () => {
      try {
        setLoading(true);
        useGedTerceirizacaoPagination.getState().setPage(1);
        await loadDocumentos({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadDocumentos,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useGedTerceirizacaoFilter.getState().clearFilters();
      useGedTerceirizacaoPagination.getState().setPage(1);
      await loadDocumentos({ ...initialGedTerceirizacaoFilterState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadDocumentos,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useGedTerceirizacaoPagination.setState({ page: page + 1 });
      await loadDocumentos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadDocumentos,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useGedTerceirizacaoPagination.setState({ page: 1, rowsPerPage });
      await loadDocumentos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadDocumentos,
    pushErrorNotification,
  ]);

  const handleRowSelectionModel = useCallback(async (model: GridRowSelectionModel) => {
    setRowSelectionModel(model);
  }, []);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedDocumento) {
      changeHistory.handleOpenChangeHistory(selectedDocumento.idArquivoModulo, LogTipoTabela.ARQUIVO_MODULO, false);
    }
  }, [
    selectedDocumento,
    changeHistory,
  ]);
  const handleActionVisualize = useCallback(async (idArquivoModulo: number) => {
    if (documentos && documentos.length > 0) {
      const arquivoModulo = documentos.find(x => x.idArquivoModulo === idArquivoModulo);
      if (arquivoModulo && arquivoModulo.nomePdf) {
        try {
          setLoading(true);
          await getGedArquivoPdfDataUseCase
            .getGedArquivoPdfData(arquivoModulo.idArquivoModulo, arquivoModulo.nomePdf);
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
        } finally {
          setLoading(false);
        }
      }
    }
  }, [
    documentos,
    getGedArquivoPdfDataUseCase,
    pushErrorNotification,
  ]);

  const handleActionDownload = useCallback(async (idArquivoModulo: number) => {
    if (documentos && documentos.length > 0) {
      const arquivoModulo = documentos.find(x => x.idArquivoModulo === idArquivoModulo);
      if (arquivoModulo && arquivoModulo.nome) {
        try {
          setLoading(true);
          await getGedArquivoDataUseCase
            .getGedArquivoData(arquivoModulo.idArquivoModulo, arquivoModulo.nome);
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
        } finally {
          setLoading(false);
        }
      }
    }
  }, [
    documentos,
    getGedArquivoDataUseCase,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    openCadastroModal,
    openDeleteModal,
    openCopyModal,
    errorMessage,
    documentos: documentos ?? [],
    selectedDocumento: selectedDocumento ?? null,
    rowSelectionModel,
    changeHistory,
    filters,
    pagination,
    handleDocumentoSubmit,
    handleDocumentoCopySubmit,
    handleReload,
    handleCadastroDocumentoOpenState,
    handleDeleteDocumentoOpenState,
    handleConfirmarDeleteDocumento,
    handleCopyDocumentoOpenState,
    handleDownloadFile,
    handleDownloadFilePdf,
    handleTipoPesquisaChange,
    handleValueFieldChange,
    handleStatusChange,
    handleClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleRowSelectionModel,
    handleOpenChangeHistory,
    handleActionVisualize,
    handleActionDownload,
  };
}